import React from 'react'
import { cn } from '../../utils/cn'

interface ModernInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  required?: boolean
  invalid?: boolean
}

export const ModernInput: React.FC<ModernInputProps> = ({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  required,
  invalid,
  className,
  id,
  ...props
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`
  const isInvalid = Boolean(invalid || error)
  const errorMessage = typeof error === 'string' ? error : undefined

  return (
    <div className="space-y-1">
      {label && (
        <div>
          <label htmlFor={inputId} className="text-sm font-medium text-foreground">
            {label}
          </label>
          {required && <span className="ml-1 text-sm text-destructive" aria-hidden="true">*</span>}
        </div>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <div className="h-5 w-5 text-muted-foreground">{leftIcon}</div>
          </div>
        )}

        <input
          id={inputId}
          className={cn(
            'block w-full rounded-md border px-3 py-2.5 shadow-sm',
            'bg-card text-foreground placeholder:text-muted-foreground',
            'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent',
            'disabled:cursor-not-allowed disabled:bg-surface disabled:text-muted-foreground',
            'transition-colors duration-150',
            isInvalid ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            className,
          )}
          {...props}
        />

        {rightIcon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <div className="h-5 w-5 text-muted-foreground">{rightIcon}</div>
          </div>
        )}
      </div>

      {errorMessage && <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>}

      {helperText && !errorMessage && <p className="text-sm text-muted-foreground">{helperText}</p>}
    </div>
  )
}

interface ModernSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  helperText?: string
  options?: Array<{ value: string; label: string }>
  placeholder?: string
  required?: boolean
}

export const ModernSelect: React.FC<ModernSelectProps> = ({
  label,
  error,
  helperText,
  options = [],
  placeholder = 'Selecione...',
  required,
  className,
  id,
  children,
  ...props
}) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`
  const errorMessage = typeof error === 'string' ? error : undefined

  return (
    <div className="space-y-1">
      {label && (
        <div>
          <label htmlFor={selectId} className="text-sm font-medium text-foreground">
            {label}
          </label>
          {required && <span className="ml-1 text-sm text-destructive" aria-hidden="true">*</span>}
        </div>
      )}

      <div className="relative">
        <select
          id={selectId}
          className={cn(
            'block w-full appearance-none rounded-md border px-3 py-2.5 pr-8 shadow-sm',
            'bg-card text-foreground',
            'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent',
            'disabled:cursor-not-allowed disabled:bg-surface disabled:text-muted-foreground',
            'transition-colors duration-150',
            error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border',
            className,
          )}
          {...props}
        >
          {children || (
            <>
              {placeholder && (
                <option value="" className="bg-card text-foreground">
                  {placeholder}
                </option>
              )}
              {options.map((option) => (
                <option key={option.value} value={option.value} className="bg-card text-foreground">
                  {option.label}
                </option>
              ))}
            </>
          )}
        </select>

        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5">
          <svg className="h-4 w-4 text-muted-foreground" fill="none" viewBox="0 0 20 20">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4" />
          </svg>
        </div>
      </div>

      {errorMessage && <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>}

      {helperText && !errorMessage && <p className="text-sm text-muted-foreground">{helperText}</p>}
    </div>
  )
}

interface ModernTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
  required?: boolean
}

export const ModernTextarea: React.FC<ModernTextareaProps> = ({
  label,
  error,
  helperText,
  required,
  className,
  id,
  ...props
}) => {
  const textareaId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`
  const errorMessage = typeof error === 'string' ? error : undefined

  return (
    <div className="space-y-1">
      {label && (
        <div>
          <label htmlFor={textareaId} className="text-sm font-medium text-foreground">
            {label}
          </label>
          {required && <span className="ml-1 text-sm text-destructive" aria-hidden="true">*</span>}
        </div>
      )}

      <textarea
        id={textareaId}
        className={cn(
          'block w-full resize-vertical rounded-md border px-3 py-2.5 shadow-sm',
          'bg-card text-foreground placeholder:text-muted-foreground',
          'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent',
          'disabled:cursor-not-allowed disabled:bg-surface disabled:text-muted-foreground',
          'transition-colors duration-150',
          error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-border',
          className,
        )}
        {...props}
      />

      {errorMessage && <p className="text-sm text-red-600 dark:text-red-400">{errorMessage}</p>}

      {helperText && !errorMessage && <p className="text-sm text-muted-foreground">{helperText}</p>}
    </div>
  )
}
